import { Deferred, Form, Head, Link, router, usePage } from "@inertiajs/react"
import { Flame, Inbox } from "lucide-react"
import { type ReactNode, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  CoverHeroCard,
  type CoverHeroSocialProof,
} from "@/components/collab-card"
import { FirstMissionSkeleton } from "@/components/onboarding/first-mission-skeleton"
import { ImmersiveMissionAccepted } from "@/components/onboarding/immersive-mission-accepted"
import { OnboardingTeaserGrid } from "@/components/onboarding/onboarding-teaser-grid"
import { WelcomeBrandStrip } from "@/components/onboarding/welcome-brand-strip"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import type { WizardStepKey } from "@/components/wizard/wizard-progress"
import { WizardShell } from "@/components/wizard/wizard-shell"
import {
  acceptOnboardingWelcomePath,
  completeOnboardingWelcomePath,
  onboardingTiktokPath,
} from "@/routes"
import type { SharedProps } from "@/types"

export interface WizardWelcomeBrand {
  id?: number
  name: string
  logo_url: string | null
  locale: "en" | "fr"
}

export interface WizardWelcomeUserSummary {
  first_name: string
  /** The active brand at completion time (Brand B on the second-brand path). */
  first_brand_name: string
  /** Optional display name for the subtitle copy when present. */
  display_name?: string
}

/** OKS-468 — the assigned first-mission payload (deferred). `null` → edge. */
export interface FirstMissionPayload {
  collab_id: number
  mission: {
    id: number
    title: string
    cover_image_url: string | null
    type: string
  }
  social_proof: CoverHeroSocialProof
}

export interface WizardWelcomeProps {
  brand: WizardWelcomeBrand
  required_steps: readonly WizardStepKey[]
  user_summary: WizardWelcomeUserSummary
  /** OKS-304 — brand cover, reused as the immersive-success background. */
  cover_image_url: string | null
  /**
   * OKS-468 — the assigned first mission. DEFERRED: `undefined` while the
   * Inertia follow-up roundtrip is in flight (→ skeleton), `null` once
   * resolved with no assigned mission (→ edge), or the payload when present.
   */
  first_mission?: FirstMissionPayload | null
  /** OKS-468 — true on the 303 follow-up after a successful Accept. */
  mission_accepted: boolean
}

/**
 * Wizard Step 4 — Welcome → first-mission handoff (§04, OKS-468).
 *
 * Evolves the OKS-384 confirmation screen into a handoff: a compact brand
 * strip, an intro, a 2×2 teaser of the crew economies, then the creator's
 * ASSIGNED first mission (deferred) as a cover-hero card with social proof +
 * soft FOMO and an Accept CTA. Accepting renders the full-bleed immersive
 * success state; every state offers a forward exit to crew home so the creator
 * is never trapped, and any forward exit completes the tour (shows once).
 *
 * Streak framing is post-based throughout — Accept never claims an active
 * streak (streaks start on the first POST).
 */
export default function Welcome({
  brand,
  required_steps,
  user_summary,
  cover_image_url,
  first_mission,
  mission_accepted,
}: WizardWelcomeProps) {
  const { t } = useTranslation()
  const headingRef = useRef<HTMLHeadingElement>(null)

  const page = usePage<Partial<SharedProps>>()
  const showConnectCta =
    page.props.tiktok_association_optional === true &&
    page.props.creator_tiktok_connected !== true

  // Deferred-prop failure tracking. Inertia's `<Deferred>` has no error
  // callback, so we listen at the router level (mirrors Candidates/Show) and
  // swap the region for an Alert + retry. `start` clears the flag so a
  // successful retry re-shows the card.
  const [firstMissionError, setFirstMissionError] = useState(false)
  useEffect(() => {
    const offError = router.on("error", () => setFirstMissionError(true))
    const offStart = router.on("start", () => setFirstMissionError(false))
    return () => {
      offError()
      offStart()
    }
  }, [])

  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  // Immersive success is full-bleed — it deliberately escapes WizardShell.
  if (mission_accepted) {
    return (
      <>
        <Head>
          <title>
            {t("wizard.welcome.head_title", {
              brand: user_summary.first_brand_name,
            })}
          </title>
        </Head>
        <ImmersiveMissionAccepted
          brandName={brand.name}
          brandLogoUrl={brand.logo_url}
          coverImageUrl={cover_image_url}
        />
      </>
    )
  }

  return (
    <WizardShell
      brand={brand}
      step="welcome"
      required_steps={required_steps}
      cover={
        <WelcomeBrandStrip brandName={brand.name} logoUrl={brand.logo_url} />
      }
    >
      <Head>
        <title>
          {t("wizard.welcome.head_title", {
            brand: user_summary.first_brand_name,
          })}
        </title>
      </Head>
      <div
        data-testid="wizard-step-welcome"
        className="flex flex-col gap-5 py-6"
      >
        <div className="flex flex-col gap-1.5 text-center">
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="text-[1.625rem] font-semibold tracking-tight outline-none"
            data-testid="wizard-welcome-headline"
          >
            {t("wizard.welcome.intro_heading", {
              name: user_summary.first_name,
            })}
          </h1>
          <p
            className="text-muted-foreground text-[0.9375rem]"
            data-testid="wizard-welcome-subtitle"
          >
            {t("wizard.welcome.intro_subtitle")}
          </p>
        </div>

        <OnboardingTeaserGrid />

        {showConnectCta && (
          <div
            className="flex flex-col items-center gap-1.5"
            data-testid="wizard-welcome-connect-tiktok"
          >
            <Button asChild variant="secondary">
              <Link href={onboardingTiktokPath()}>
                {t("tiktok_optional.welcome.connect_cta")}
              </Link>
            </Button>
            <p className="text-muted-foreground text-xs">
              {t("tiktok_optional.welcome.helper")}
            </p>
          </div>
        )}

        {firstMissionError ? (
          <FirstMissionError
            onRetry={() => {
              setFirstMissionError(false)
              router.reload({ only: ["first_mission"] })
            }}
          />
        ) : (
          <Deferred data="first_mission" fallback={<FirstMissionSkeleton />}>
            <FirstMissionRegion
              firstMission={first_mission ?? null}
              brandName={brand.name}
            />
          </Deferred>
        )}
      </div>
    </WizardShell>
  )
}

/**
 * The resolved first-mission region: the cover-hero handoff card when a
 * mission is assigned, or the edge "no mission assigned yet" empty state
 * (by design under the assignment-based model). Both keep a forward exit.
 */
export function FirstMissionRegion({
  firstMission,
  brandName,
}: {
  firstMission: FirstMissionPayload | null
  brandName: string
}) {
  const { t } = useTranslation()

  if (firstMission === null) {
    return (
      <div
        data-testid="first-mission-edge"
        className="flex flex-col items-center gap-3 py-6 text-center"
      >
        <span className="bg-primary/10 flex size-16 items-center justify-center rounded-2xl">
          <Inbox className="text-primary size-7" aria-hidden="true" />
        </span>
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold">
            {t("wizard.welcome.edge.title")}
          </h2>
          <p className="text-muted-foreground max-w-xs text-sm">
            {t("wizard.welcome.edge.body", { brand: brandName })}
          </p>
        </div>
        <ForwardExitLink
          testId="first-mission-edge-explore"
          label={t("wizard.welcome.edge.explore")}
        />
      </div>
    )
  }

  const { collab_id, mission, social_proof } = firstMission

  return (
    <div className="flex flex-col gap-3" data-testid="first-mission-handoff">
      <p
        className="text-primary text-xs font-semibold tracking-wide uppercase"
        data-testid="first-mission-eyebrow"
      >
        {t("wizard.welcome.handoff.eyebrow")}
      </p>

      <CoverHeroCard
        coverImageUrl={mission.cover_image_url}
        title={mission.title}
        socialProof={social_proof}
        meta={t("wizard.welcome.handoff.no_deadline")}
      >
        <Form
          method="post"
          action={acceptOnboardingWelcomePath()}
          data-testid="first-mission-accept-form"
        >
          {({ processing }) => (
            <>
              <input type="hidden" name="collab_id" value={collab_id} />
              <Button
                type="submit"
                disabled={processing}
                aria-disabled={processing}
                className="w-full"
                aria-label={t("wizard.welcome.handoff.accept_aria", {
                  title: mission.title,
                })}
                data-testid="first-mission-accept"
              >
                {processing && <Spinner className="size-4" />}
                {t("wizard.welcome.handoff.accept_cta")}
              </Button>
            </>
          )}
        </Form>
      </CoverHeroCard>

      <p
        className="text-muted-foreground flex items-center justify-center gap-1.5 text-sm font-medium"
        data-testid="first-mission-streak-microline"
      >
        <Flame className="size-[15px]" aria-hidden="true" />
        {t("wizard.welcome.handoff.streak_microline")}
      </p>

      <ForwardExitLink
        testId="first-mission-see-all"
        label={t("wizard.welcome.handoff.see_all")}
      />
    </div>
  )
}

/** Deferred-failure fallback for the first-mission region (not-trapped: the
 *  forward exit stays available). */
export function FirstMissionError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-3" data-testid="first-mission-error">
      <Alert>
        <AlertDescription className="flex items-center justify-between gap-3">
          <span>{t("wizard.welcome.handoff.error")}</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onRetry}
            data-testid="first-mission-error-retry"
          >
            {t("wizard.welcome.handoff.retry")}
          </Button>
        </AlertDescription>
      </Alert>
      <ForwardExitLink
        testId="first-mission-see-all"
        label={t("wizard.welcome.handoff.see_all")}
      />
    </div>
  )
}

/**
 * Forward exit to crew home. Rendered as an Inertia `<Form>` POSTing
 * `completeOnboardingWelcomePath()` so the server stamps `tour_completed_at`
 * (the handoff shows once) and 303s to the creator's missions index. Styled
 * as a text link per the UX spec.
 */
function ForwardExitLink({
  label,
  testId,
}: {
  label: ReactNode
  testId: string
}) {
  return (
    <Form
      method="post"
      action={completeOnboardingWelcomePath()}
      className="flex justify-center"
    >
      {({ processing }) => (
        <Button
          type="submit"
          variant="link"
          disabled={processing}
          aria-disabled={processing}
          className="text-primary font-medium"
          data-testid={testId}
        >
          {label}
        </Button>
      )}
    </Form>
  )
}
